/**
 * The parsing half of the Wi-Fi transport, with nothing network in it.
 *
 * Split out from `wifi.ts` so it can be tested in node beside the rest of the
 * pure modules: the strictness of `parsePing` is exactly the sort of thing
 * that looks right and is quietly wrong, and it is the check that stops an
 * image being sent to someone else's web server.
 *
 * `parsePing` itself - ported from `_parse_ping` in ESP32-Tools'
 * `backend/otad/wifiota.py` - lives in `src/ota/endpoint.ts`, where the mobile
 * app's live board list uses it too; it is re-exported here so the web app
 * keeps one import for its transport.
 */
import { DEFAULT_PORT } from '../../../src/ota/endpoint';

export { DEFAULT_PORT, OTA_PATH, PING_PATH, parsePing } from '../../../src/ota/endpoint';
export type { PingBody } from '../../../src/ota/endpoint';

/** How long the board may take to come back after an update. */
export const COMEBACK_TIMEOUT_MS = 45_000;
export const COMEBACK_POLL_MS = 1_500;
/** Give the HTTP server a moment after the first answer, as the daemon does. */
export const COMEBACK_SETTLE_MS = 2_000;
export const PROBE_TIMEOUT_MS = 2_500;

/** "192.168.0.127" or "192.168.0.127:8080" -> a base URL. */
export function baseUrl(host: string, port: number = DEFAULT_PORT): string {
  const trimmed = host.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  if (/:\d+$/.test(trimmed)) return `http://${trimmed}`;
  return port === DEFAULT_PORT ? `http://${trimmed}` : `http://${trimmed}:${port}`;
}

/** Split whatever the user typed into a host and a port. */
export function splitHostPort(
  input: string,
  fallback: number = DEFAULT_PORT
): { host: string; port: number } {
  const trimmed = input.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  const m = /^(.*):(\d+)$/.exec(trimmed);
  if (m) return { host: m[1], port: Number(m[2]) };
  return { host: trimmed, port: fallback };
}
